import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

import type { Updater } from '@tanstack/vue-table'
import { type Ref } from 'vue'
import { TranslateOptions, useI18n } from 'vue-i18n'
import { DynCategories, TNKeys } from '@/components/lang/langHandler'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function valueUpdater<T extends Updater<unknown>>(updaterOrValue: T, ref: Ref) {
  ref.value = typeof updaterOrValue === 'function' ? updaterOrValue(ref.value) : updaterOrValue
}

export function capitalizeFirstLetter(val: string | number) {
  return String(val).charAt(0).toUpperCase() + String(val).slice(1).toLowerCase()
}

export function useTranslateKey() {
  const { t, te } = useI18n()
  /**
   * Translates the given key using vue-i18n.
   * If no translation is available, returns the original value.
   * Wrapper for useI18n
   *
   * @param value - The translation key.
   * rest of params imitated the useI18n
   * @returns The translated key if available, otherwise the original value.
   * @author fw, gk
   */
  const translateKey = (
    value: TNKeys,
    named?: Record<string, string | number>,
    options?: TranslateOptions<string>
  ) => {
    if (!value) return ''
    return t(value, named, options)
  }

  const translateKeyDyn = <S extends string>(
    category: DynCategories,
    value: string extends S ? S : 'MUST NOT USE STRING LITERAL FOR DYN',
    named?: Record<string, string | number>,
    options?: TranslateOptions<string>
  ) => {
    if (!value) return ''
    const sanitizedCategory = String(category)
      .replace(/[^\p{L}_.]/gu, '')
      .toLowerCase()
    const sanitizedKey = value.replace(/[^\p{L}_.]/gu, '').toLowerCase()
    const cleanedKey = `dyn.${sanitizedCategory}.${sanitizedKey}`
    // Check if translation exists, if not return the original unsanitized value
    if (!te(cleanedKey)) {
      return value
    }
    return t(cleanedKey, named, options)
  }

  return { translateKey, translateKeyDyn }
}

type UseTranslateKeyRet = ReturnType<typeof useTranslateKey>
export type TranslateKeyFn = UseTranslateKeyRet['translateKey']
export type TranslateKeyDynFn = UseTranslateKeyRet['translateKeyDyn']

/**
 * Formats a date using German Austrian locale
 * @param {string | number | Date} date - The date to format (can be string, number timestamp, or Date object)
 * @returns {string} Formatted date string in de-AT locale with year, month, day, hour, and minute
 * @example
 * formatDate('2024-01-15T14:30:00Z') // '15.1.2024, 15:30'
 * formatDate(1705328400000) // '15.1.2024, 15:30'
 * formatDate(new Date()) // Current date formatted
 */
export const formatDate = (date: string | number | Date): string => {
  return new Date(date).toLocaleDateString('de-AT', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

/**
 * Formats a date using German Austrian locale (date only, no time)
 * @param date - The date to format (can be string, number timestamp, or Date object)
 * @returns Formatted date string in de-AT locale with only year, month, and day
 * @example
 * formatDateOnly('2024-01-15T14:30:00Z') // '15.1.2024'
 * formatDateOnly(1705328400000) // '15.1.2024'
 * formatDateOnly(new Date()) // Current date formatted
 */
export const formatDateOnly = (date: string | number | Date): string => {
  return new Date(date).toLocaleDateString('de-AT', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
  })
}

export function removeTeilPromptingPlaceholders(html: string): string {
  const asyncPlaceholderPattern = /<async-placeholder[^>]*>.*?<\/async-placeholder>/gs

  const decodeEntities = (input: string) =>
    input
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&')

  return html.replace(asyncPlaceholderPattern, match => {
    const attrMatch = match.match(/data-content-snapshot=(['"])(.*?)\1/i)
    if (!attrMatch) return match
    return decodeEntities(attrMatch[2])
  })
}

export const stripHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return doc.body.textContent || ''
}
